/**
 * Admin → Bulk Notifications (iter134r).
 *
 * Lets tenant admins fire an FCM + web-push blast to targeted user
 * cohorts (Customers / Riders / Merchants / Admins) filtered by
 * Zone / Shop / individual users, or "all of a role". Preview shows
 * a live audience count + sample names before the admin taps Send.
 * Every dispatched run is logged in a history table below the form.
 *
 * iter182: a send is one run on the server. A large audience answers 202 at once and the page polls the run until
 * it ends (it used to wait for the whole fan-out, outlive the app's 20 s request timeout and show "Send failed"
 * while the push was still going out). The result panel shows who it reached, and failures show the server's own
 * reason. Each campaign carries an idempotency key, so a retry never pushes it twice.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Loader2, Send, Eye, Users, Bike, Store, ShieldCheck,
    Image as ImageIcon, Link as LinkIcon, RefreshCw, Check, AlertTriangle,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useSubmitGuard } from "../../lib/useSubmitGuard";
import {
    MESSAGE_LIMIT, TITLE_LIMIT, describeApiError, isInFlight, newIdempotencyKey, statusInfo, summarizeRun,
    validateDeepLink, validateImageUrl,
} from "../../lib/bulkNotify";

const POLL_MS = 1500;
const POLL_GIVE_UP_AFTER_FAILURES = 5;
// The server closes a run whose worker stopped within ~3 minutes, so a poll this long means something else is
// wrong; stop rather than spin for ever (the result stays available in "Recent bulk sends").
const POLL_MAX_MS = 20 * 60 * 1000;

const ROLE_OPTIONS = [
    { value: "CUSTOMER",         label: "Customers",              icon: Users,       app: "customer" },
    { value: "RIDER",            label: "Riders",                 icon: Bike,        app: "rider" },
    { value: "RESTAURANT_OWNER", label: "Merchants / Shop Owners", icon: Store,       app: null },
    { value: "TENANT_ADMIN",     label: "Admins",                 icon: ShieldCheck, app: null },
];

// Which scopes each role supports — mirrors the backend
// ``_ALLOWED_COMBOS`` so the frontend can't emit an unsupported combo.
const SCOPES_FOR_ROLE = {
    CUSTOMER:         ["ALL", "SHOP", "USERS"],
    RIDER:            ["ALL", "USERS"],
    RESTAURANT_OWNER: ["ALL", "ZONE", "SHOP", "USERS"],
    TENANT_ADMIN:     ["ALL", "USERS"],
};
const SCOPE_LABEL = {
    ALL:   "All users of this role",
    ZONE:  "By Zone (shops in the zone)",
    SHOP:  "By specific Shop",
    USERS: "Individual / multiple users",
};

export default function AdminBulkNotifications() {
    // ---- Targeting form state
    const [role, setRole] = useState("CUSTOMER");
    const [scope, setScope] = useState("ALL");
    const [zoneId, setZoneId] = useState("");
    const [shopId, setShopId] = useState("");
    const [userIdInput, setUserIdInput] = useState("");   // comma-separated

    // ---- Payload state
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [deepLink, setDeepLink] = useState("");

    // ---- Async state
    const [zones, setZones] = useState([]);
    const [shops, setShops] = useState([]);
    const [previewing, setPreviewing] = useState(false);
    const [preview, setPreview] = useState(null);       // {count, sample, role, scope}
    // Bug found live: window.confirm() blocks a raw double-click, but
    // has the same async-render race as every other button this app
    // guards with useSubmitGuard (see that file's docstring) — a fast
    // enough second click + confirm could still slip through and fire
    // two independent campaigns (each mints its own doc_id, so the
    // backend's per-recipient dedup doesn't catch it — see the 30s
    // duplicate-send guard added server-side too).
    const [sending, doSendGuarded] = useSubmitGuard();
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [run, setRun] = useState(null);               // the run started from this screen
    const [sendError, setSendError] = useState(null);
    // One key per campaign: kept across retries of the same content (a timeout, a lost answer), renewed when the
    // content or audience changes and after a run ends.
    const keyRef = useRef(newIdempotencyKey());
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;          // set again on StrictMode's remount
        return () => { mounted.current = false; };
    }, []);

    // Load zones + shops so the filter dropdowns have real options.
    useEffect(() => {
        (async () => {
            try {
                const [zRes, sRes] = await Promise.all([
                    api.get("/admin/zones"),
                    api.get("/admin/restaurants"),
                ]);
                setZones(zRes.data || []);
                setShops(sRes.data || []);
            } catch (err) {
                console.debug("[AdminBulk] zones/shops load failed", err);
            }
        })();
        reloadHistory();
    }, []);

    const reloadHistory = async () => {
        setLoadingHistory(true);
        try {
            const { data } = await api.get("/admin/bulk-notifications?limit=50");
            setHistory(data || []);
        } catch (err) {
            console.debug("[AdminBulk] history load failed", err);
        } finally {
            setLoadingHistory(false);
        }
    };

    // Reset the scope-specific inputs whenever the role changes so the
    // form can't accidentally submit stale zone_id/shop_id from a
    // previous role selection.
    useEffect(() => {
        setScope(SCOPES_FOR_ROLE[role][0]);
        setZoneId(""); setShopId(""); setUserIdInput(""); setPreview(null);
    }, [role]);
    useEffect(() => { setPreview(null); }, [scope, zoneId, shopId, userIdInput]);
    useEffect(() => {
        keyRef.current = newIdempotencyKey();
    }, [role, scope, zoneId, shopId, userIdInput, title, body, imageUrl, deepLink]);

    const imageError = validateImageUrl(imageUrl);
    const deepLinkError = validateDeepLink(deepLink);

    const parsedUserIds = useMemo(() =>
        userIdInput.split(/[,\s\n]+/).map((s) => s.trim()).filter(Boolean),
    [userIdInput]);

    const buildTarget = () => {
        const t = { role, scope };
        if (scope === "ZONE")  t.zone_id  = zoneId;
        if (scope === "SHOP")  t.shop_id  = shopId;
        if (scope === "USERS") t.user_ids = parsedUserIds;
        return t;
    };

    const canSubmit = () => {
        if (!title.trim() || !body.trim()) return false;
        if (imageError || deepLinkError) return false;
        if (scope === "ZONE"  && !zoneId)  return false;
        if (scope === "SHOP"  && !shopId)  return false;
        if (scope === "USERS" && parsedUserIds.length === 0) return false;
        return true;
    };

    const doPreview = async () => {
        if (!canSubmit()) {
            toast.error(imageError || deepLinkError || "Fill in title, message and the required filter first");
            return;
        }
        setPreviewing(true);
        try {
            const { data } = await api.post("/admin/bulk-notifications/preview", {
                target: buildTarget(),
                title: title.trim(),
                body: body.trim(),
                image_url: imageUrl.trim() || undefined,
                deep_link: deepLink.trim() || undefined,
            });
            setPreview(data);
            if (data.count === 0) {
                toast.warning("No recipients matched — refine your filter");
            } else if (data.with_device === 0) {
                toast.warning("None of these recipients has a registered device — nobody would receive it");
            }
        } catch (e) {
            toast.error(describeApiError(e, "Preview").message);
        } finally {
            setPreviewing(false);
        }
    };

    // A run has ended: tell the admin how it went, refresh the history, and start a fresh campaign. The form is
    // kept when nobody received it, so it can be corrected and sent again.
    const finishRun = useCallback((data) => {
        const info = statusInfo(data.status);
        const s = data.summary || {};
        const line = `${s.recipients_accepted ?? 0} of ${s.target_count ?? 0} accepted for delivery`;
        if (info.tone === "success") toast.success(`${info.label} — ${line}`, { duration: 8000 });
        else if (info.tone === "error") toast.error(data.failure_message || `${info.label} — ${line}`, { duration: 10000 });
        else toast.warning(data.failure_message || `${info.label} — ${line}`, { duration: 10000 });
        keyRef.current = newIdempotencyKey();
        if (data.status === "SENT" || data.status === "PARTIAL") {
            setTitle(""); setBody(""); setImageUrl(""); setDeepLink("");
            setPreview(null);
        }
        reloadHistory();
    }, []);

    // Follow a run that is still going out.
    useEffect(() => {
        // ``lost``: this screen stopped following the run (see stopFollowing) — its status may still say SENDING.
        if (!run?.id || run.lost || !isInFlight(run)) return undefined;
        let failures = 0;
        let timer;
        const startedAt = Date.now();
        const stopFollowing = (message) => {
            setSendError(message);
            setRun((r) => (r ? { ...r, in_progress: false, lost: true } : r));
        };
        const tick = async () => {
            if (Date.now() - startedAt > POLL_MAX_MS) {
                if (mounted.current) {
                    stopFollowing("This send is taking unusually long. It keeps going out on the server — refresh "
                        + "“Recent bulk sends” for its result.");
                }
                return;
            }
            try {
                const { data } = await api.get(`/admin/bulk-notifications/${run.id}`);
                if (!mounted.current) return;
                failures = 0;
                setRun(data);
                if (!isInFlight(data)) { finishRun(data); return; }
            } catch (e) {
                failures += 1;
                if (failures >= POLL_GIVE_UP_AFTER_FAILURES) {
                    if (mounted.current) {
                        stopFollowing(`${describeApiError(e, "Status check").message} The push keeps going out on the `
                            + "server — refresh “Recent bulk sends” for its result.");
                    }
                    return;
                }
            }
            if (mounted.current) timer = setTimeout(tick, POLL_MS);
        };
        timer = setTimeout(tick, POLL_MS);
        return () => clearTimeout(timer);
        // Re-arm only when a different run starts; each tick schedules the next.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run?.id, run?.lost]);

    const runBusy = isInFlight(run) && !run?.lost;

    const doSend = () => {
        if (!preview) {
            toast.error("Preview the audience first");
            return;
        }
        if (preview.count === 0) {
            toast.error("Zero recipients — nothing to send");
            return;
        }
        if (preview.with_device === 0) {
            toast.error("None of these recipients has a registered device — nothing to send");
            return;
        }
        if (runBusy) return;
        const reach = typeof preview.with_device === "number"
            ? ` ${preview.with_device} of them have a registered device.` : "";
        if (!window.confirm(`Send this push to ${preview.count} recipient(s)?${reach} This cannot be undone.`)) return;
        doSendGuarded(async () => {
            setSendError(null);
            try {
                const { data } = await api.post("/admin/bulk-notifications/send", {
                    target: buildTarget(),
                    title: title.trim(),
                    body: body.trim(),
                    image_url: imageUrl.trim() || undefined,
                    deep_link: deepLink.trim() || undefined,
                    idempotency_key: keyRef.current,
                });
                if (!mounted.current) return;
                setRun(data);
                if (data.deduped_as_recent_duplicate) {
                    toast.info("Already sent — this exact campaign went out (or is going out) already, so this tap "
                        + "didn't push it again.", { duration: 6000 });
                }
                if (!isInFlight(data)) finishRun(data);
            } catch (e) {
                const { message, maybeRunning } = describeApiError(e);
                setSendError(message);
                toast.error(message, { duration: 10000 });
                if (maybeRunning) reloadHistory();
            }
        });
    };

    return (
        <div className="pb-16" data-testid="admin-bulk-notifications">
            <h1 className="text-2xl font-bold">Bulk Notifications</h1>
            <p className="text-sm text-muted-foreground mt-1">
                Fire an FCM + web-push blast to targeted user cohorts. Preview the audience before you send — every run is logged below.
            </p>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr,380px] gap-6 items-start">
                {/* ---------------- Composer ---------------- */}
                <div className="bg-card border border-border rounded-2xl p-5 space-y-5">
                    <section>
                        <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
                            1 · Target audience
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                            {ROLE_OPTIONS.map((r) => {
                                const active = role === r.value;
                                const Icon = r.icon;
                                return (
                                    <button
                                        key={r.value}
                                        type="button"
                                        data-testid={`role-${r.value}`}
                                        onClick={() => setRole(r.value)}
                                        className={`rounded-xl border px-3 py-3 text-left transition ${
                                            active
                                                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                                : "border-border hover:border-primary/40"
                                        }`}
                                    >
                                        <Icon size={16} className={active ? "text-primary" : "text-muted-foreground"} />
                                        <p className="text-xs font-bold mt-1.5">{r.label}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section>
                        <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
                            2 · Filter
                        </p>
                        <select
                            data-testid="scope-select"
                            className="inp mt-2"
                            value={scope}
                            onChange={(e) => setScope(e.target.value)}
                        >
                            {SCOPES_FOR_ROLE[role].map((s) => (
                                <option key={s} value={s}>{SCOPE_LABEL[s]}</option>
                            ))}
                        </select>

                        {scope === "ZONE" && (
                            <div className="mt-3">
                                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Zone</label>
                                <select
                                    data-testid="zone-select"
                                    className="inp mt-1"
                                    value={zoneId}
                                    onChange={(e) => setZoneId(e.target.value)}
                                >
                                    <option value="">— pick a zone —</option>
                                    {zones.map((z) => (
                                        <option key={z.id} value={z.id}>{z.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {scope === "SHOP" && (
                            <div className="mt-3">
                                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Shop</label>
                                <select
                                    data-testid="shop-select"
                                    className="inp mt-1"
                                    value={shopId}
                                    onChange={(e) => setShopId(e.target.value)}
                                >
                                    <option value="">— pick a shop —</option>
                                    {shops.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                                {role === "CUSTOMER" && (
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Targets every customer who has placed an order at this shop.
                                    </p>
                                )}
                                {role === "RESTAURANT_OWNER" && (
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Targets the owner(s) linked to this shop.
                                    </p>
                                )}
                            </div>
                        )}

                        {scope === "USERS" && (
                            <div className="mt-3">
                                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                    {role === "RIDER" ? "Rider IDs" : "User IDs"}
                                </label>
                                <textarea
                                    data-testid="user-ids-input"
                                    className="inp mt-1 font-mono text-xs"
                                    rows={3}
                                    placeholder="One ID per line — or comma-separated."
                                    value={userIdInput}
                                    onChange={(e) => setUserIdInput(e.target.value)}
                                />
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    Paste one or more {role === "RIDER" ? "rider" : "user"} IDs (UUIDs). Detected: <b>{parsedUserIds.length}</b>.
                                </p>
                            </div>
                        )}
                    </section>

                    <section>
                        <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
                            3 · Push content
                        </p>
                        <div className="mt-2 space-y-3">
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Title (1–80 chars)</label>
                                <input
                                    data-testid="push-title"
                                    className="inp mt-1"
                                    maxLength={TITLE_LIMIT}
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="e.g. 30% off — today only"
                                />
                                <p className="text-[11px] text-muted-foreground mt-0.5">{title.length}/{TITLE_LIMIT}</p>
                            </div>
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Message (1–240 chars)</label>
                                <textarea
                                    data-testid="push-body"
                                    rows={3}
                                    className="inp mt-1"
                                    maxLength={MESSAGE_LIMIT}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    placeholder="Weekend Feast is live — 30% off every order over ₹199. Ends midnight."
                                />
                                <p className="text-[11px] text-muted-foreground mt-0.5">{body.length}/{MESSAGE_LIMIT}</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                                        <ImageIcon size={11} /> Image URL (optional)
                                    </label>
                                    <input
                                        data-testid="push-image"
                                        type="url"
                                        className="inp mt-1"
                                        value={imageUrl}
                                        onChange={(e) => setImageUrl(e.target.value)}
                                        placeholder="https://…/hero.png"
                                        aria-invalid={!!imageError}
                                    />
                                    {imageError && (
                                        <p data-testid="push-image-error" className="text-[11px] text-destructive mt-1">{imageError}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                                        <LinkIcon size={11} /> Deep link (optional)
                                    </label>
                                    <input
                                        data-testid="push-deeplink"
                                        className="inp mt-1"
                                        value={deepLink}
                                        onChange={(e) => setDeepLink(e.target.value)}
                                        placeholder="/coupons  or  /restaurant/abc123"
                                        aria-invalid={!!deepLinkError}
                                    />
                                    {deepLinkError && (
                                        <p data-testid="push-deeplink-error" className="text-[11px] text-destructive mt-1">{deepLinkError}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 pt-2">
                        <button
                            type="button"
                            data-testid="preview-btn"
                            onClick={doPreview}
                            disabled={previewing || !canSubmit()}
                            className="w-full sm:w-auto justify-center rounded-xl border border-primary text-primary bg-primary/5 hover:bg-primary/10 px-5 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {previewing ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />} Preview audience
                        </button>
                        <button
                            type="button"
                            data-testid="send-btn"
                            onClick={doSend}
                            disabled={sending || runBusy || !preview || preview.count === 0 || preview.with_device === 0}
                            aria-busy={sending || runBusy}
                            className="w-full sm:w-auto justify-center rounded-xl bg-primary text-primary-foreground hover:opacity-95 px-5 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {(sending || runBusy) ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            {(sending || runBusy) ? "Sending…" : "Send now"}
                        </button>
                        {preview && (
                            <span
                                data-testid="preview-count"
                                className="self-start sm:self-auto sm:ml-auto inline-flex flex-wrap items-center gap-1 text-sm font-semibold rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 px-3 py-1.5"
                            >
                                <Check size={13} /> {preview.count} recipient(s)
                                {typeof preview.with_device === "number" && (
                                    <span data-testid="preview-with-device" className="font-normal">
                                        · {preview.with_device} with a device
                                    </span>
                                )}
                            </span>
                        )}
                    </div>

                    {sendError && (
                        <div
                            role="alert"
                            data-testid="send-error"
                            className="rounded-xl border border-destructive/40 bg-destructive/5 text-destructive text-sm px-4 py-3 flex gap-2"
                        >
                            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                            <span className="min-w-0">{sendError}</span>
                        </div>
                    )}
                    {run && <SendResult run={run} />}
                </div>

                {/* ---------------- Live preview ---------------- */}
                <aside className="space-y-4">
                    <div className="bg-card border border-border rounded-2xl p-5">
                        <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Push preview</p>
                        <PushPreviewCard
                            title={title || "Notification title"}
                            body={body || "This is where your message shows up on the recipient's phone."}
                            imageUrl={imageUrl}
                            deepLink={deepLink}
                        />
                    </div>
                    {preview && (
                        <div className="bg-card border border-border rounded-2xl p-5" data-testid="audience-sample">
                            <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Audience sample</p>
                            {preview.sample?.length ? (
                                <ul className="mt-2 text-sm space-y-1">
                                    {preview.sample.map((s) => (
                                        <li key={s.id} className="flex items-center justify-between gap-2 border-b border-border/50 last:border-0 py-1">
                                            <span className="truncate">{s.label}</span>
                                            <code className="text-[10px] text-muted-foreground">{s.id.slice(0, 8)}…</code>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-muted-foreground mt-2">No matching recipients.</p>
                            )}
                            {preview.count > (preview.sample?.length || 0) && (
                                <p className="text-[11px] text-muted-foreground mt-2">
                                    + {preview.count - (preview.sample?.length || 0)} more not shown.
                                </p>
                            )}
                        </div>
                    )}
                </aside>
            </div>

            {/* ---------------- History ---------------- */}
            <section className="mt-8" data-testid="bulk-history-section">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold">Recent bulk sends</h2>
                    <button
                        type="button"
                        onClick={reloadHistory}
                        disabled={loadingHistory}
                        className="text-xs font-semibold inline-flex items-center gap-1.5 rounded-lg bg-muted hover:bg-muted/80 px-3 py-1.5 disabled:opacity-60"
                        data-testid="refresh-history"
                    ><RefreshCw size={13} className={loadingHistory ? "animate-spin" : ""} /> Refresh</button>
                </div>
                <div className="mt-3 bg-card border border-border rounded-2xl overflow-hidden">
                    {loadingHistory && (
                        <p className="px-3 py-6 text-center text-sm text-muted-foreground"><Loader2 size={14} className="inline animate-spin mr-1" /> Loading…</p>
                    )}
                    {!loadingHistory && history.length === 0 && (
                        <p className="px-3 py-6 text-center text-sm text-muted-foreground">No bulk sends yet.</p>
                    )}
                    {/* Phones + tablets in portrait */}
                    {!loadingHistory && history.length > 0 && (
                        <ul className="lg:hidden divide-y divide-border/60">
                            {history.map((h) => {
                                const sum = summarizeRun(h);
                                return (
                                    <li key={h.id} className="px-4 py-3" data-testid={`history-card-${h.id}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="font-semibold text-sm min-w-0 break-words">{h.title}</p>
                                            <StatusChip status={h.status} />
                                        </div>
                                        <p className="text-[11px] text-muted-foreground mt-0.5">
                                            {fmtTs(h.created_at)} · {h.target?.role || "—"} · {h.target?.scope || "—"} · {h.created_by_name || "—"}
                                        </p>
                                        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                                            {sum.rows.map((r) => (
                                                <div key={r.key} className="flex justify-between gap-2 min-w-0">
                                                    <dt className="text-muted-foreground truncate">{r.label}</dt>
                                                    <dd className="font-mono">{r.value}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                        {h.failure_message && (
                                            <p className="text-[11px] text-muted-foreground mt-1.5">{h.failure_message}</p>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {/* Tablets in landscape + desktop */}
                    {!loadingHistory && history.length > 0 && (
                    <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left px-3 py-2">Sent at</th>
                                <th className="text-left px-3 py-2">Title</th>
                                <th className="text-left px-3 py-2">Target</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-right px-3 py-2">Recipients</th>
                                <th className="text-right px-3 py-2">Accepted / failed / no device</th>
                                <th className="text-right px-3 py-2">FCM / Web</th>
                                <th className="text-right px-3 py-2">By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((h) => {
                                const s = h.summary || {};
                                const sum = summarizeRun(h);
                                const val = (k) => sum.rows.find((r) => r.key === k)?.value ?? 0;
                                return (
                                    <tr key={h.id} className="border-t border-border/50 align-top" data-testid={`history-${h.id}`}>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtTs(h.created_at)}</td>
                                        <td className="px-3 py-2 font-semibold">
                                            {h.title}
                                            {h.failure_message && (
                                                <span className="block text-[11px] font-normal text-muted-foreground max-w-md">{h.failure_message}</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            {h.target?.role || "—"}
                                            <span className="text-muted-foreground"> · {h.target?.scope || "—"}</span>
                                        </td>
                                        <td className="px-3 py-2"><StatusChip status={h.status} /></td>
                                        <td className="px-3 py-2 text-right font-mono">{h.target_count ?? "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">
                                            {sum.legacy ? "—" : <>{val("accepted")}<span className="text-muted-foreground"> / </span>{val("failed")}<span className="text-muted-foreground"> / </span>{val("no_device")}</>}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-xs">
                                            {(s.sent_fcm ?? 0)}<span className="text-muted-foreground">/</span>{(s.failed_fcm ?? 0)}
                                            <span className="text-muted-foreground"> · </span>
                                            {(s.sent_web ?? 0)}<span className="text-muted-foreground">/</span>{(s.failed_web ?? 0)}
                                            {sum.legacy && !!s.no_device && (
                                                <span className="block text-[10px] text-muted-foreground normal-case font-sans">
                                                    {s.no_device} no device
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs text-muted-foreground">{h.created_by_name || "—"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                    )}
                </div>
            </section>

            <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.6rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
        </div>
    );
}

const TONE_CLASS = {
    success: "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200",
    warning: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
    error: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-200",
    info: "bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200",
};

function StatusChip({ status }) {
    const info = statusInfo(status);
    return (
        <span className={`shrink-0 whitespace-nowrap inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE_CLASS[info.tone]}`}>
            {info.label}
        </span>
    );
}

// The run started from this screen: live while it goes out, then its outcome.
function SendResult({ run }) {
    const sum = summarizeRun(run);
    const inFlight = isInFlight(run) && !run.lost;
    return (
        <div data-testid="send-result" aria-live="polite" className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-bold inline-flex items-center gap-2">
                    {inFlight && <Loader2 size={14} className="animate-spin" />}
                    {inFlight ? `Sending… ${sum.processed ?? 0} of ${sum.total} processed` : "Last send"}
                </p>
                <StatusChip status={run.status} />
            </div>
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {sum.rows.map((r) => (
                    <div key={r.key} className="rounded-lg bg-card border border-border px-3 py-2 min-w-0" data-testid={`send-result-${r.key}`}>
                        <dt className="text-[11px] text-muted-foreground leading-tight">{r.label}</dt>
                        <dd className="text-lg font-bold font-mono">{r.value}</dd>
                    </div>
                ))}
            </dl>
            {run.failure_message && (
                <p data-testid="send-result-message" className="text-xs text-foreground/80">{run.failure_message}</p>
            )}
            <p className="text-[11px] text-muted-foreground">
                “Accepted for delivery” means Firebase or the browser’s push service took the message. Phones don’t
                report back when it is shown, so delivery itself can’t be confirmed here.
            </p>
        </div>
    );
}

// A phone-notification-style preview card so the admin sees the push
// roughly how the recipient will (Android + web behave the same).
function PushPreviewCard({ title, body, imageUrl, deepLink }) {
    return (
        <div
            data-testid="push-preview-card"
            className="mt-3 rounded-2xl border border-border bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-950 p-3"
        >
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground inline-flex items-center justify-center text-sm font-bold shrink-0">
                    B
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-muted-foreground">Bizil · now</p>
                    <p className="text-sm font-bold mt-0.5 line-clamp-2">{title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-3 whitespace-pre-wrap">{body}</p>
                    {deepLink && (
                        <p className="text-[10px] text-primary mt-1.5 truncate">
                            <LinkIcon size={9} className="inline mr-0.5" /> {deepLink}
                        </p>
                    )}
                </div>
            </div>
            {imageUrl && (
                <div className="mt-2 rounded-xl overflow-hidden border border-border">
                    <img
                        src={imageUrl}
                        alt="Push preview"
                        loading="lazy"
                        className="w-full h-32 object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                </div>
            )}
        </div>
    );
}

function fmtTs(ts) {
    if (!ts) return "—";
    try {
        const d = new Date(ts);
        return d.toLocaleString([], { dateStyle: "short", timeStyle: "short" });
    } catch { return ts; }
}
